import ReactDOM from 'react-dom';
import { BrowserRouter as Router } from 'react-router-dom';

import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthProvider } from '@/contexts/AuthProvider';
import { ModalsProvider } from '@/contexts/ModalsProvider';
import { PartnerRoleProvider } from '@/contexts/PartnerRoleProvider';
import { ThemeProvider } from '@/contexts/ThemeContextProvider';

import { ErrorBoundary } from '@/components/errors/ErrorBoundary';

import { APP_ZOOM } from '@/styles/portalLayout';

import { App } from './App';
import { LOCAL } from './constants';
import { queryClient } from './queryClient';

import './i18n';
import './scripts/dayjs';
import './mixpanel';

if (!LOCAL && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js');
  });
}

/**
 * Mounts the app.
 * `QueryClientProvider` is the outermost provider, above `AuthProvider`, so every provider and
 * page can read saved server data (React Query) and `logout()` can clear it through the same
 * `queryClient` singleton.
 * `ReactQueryDevtools` is a development tool: `@tanstack/react-query-devtools` v4 renders nothing
 * unless `process.env.NODE_ENV === 'development'`, and the production webpack config
 * (`@altirllc/shared/webpack`, mode `production`, `sideEffects: false` in the package) drops its
 * code from the bundle.
 */
ReactDOM.render(
  <QueryClientProvider client={queryClient}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider>
        <CssBaseline />
        <GlobalStyles
          styles={{
            html: {
              zoom: APP_ZOOM,
              // Firefox fallback (doesn't support zoom)
              '@supports not (zoom: 1)': {
                transform: `scale(${APP_ZOOM})`,
                transformOrigin: 'top left',
                width: `${100 / APP_ZOOM}%`,
              },
            },
          }}
        />
        <AuthProvider>
          <PartnerRoleProvider>
            <Router>
              <ErrorBoundary>
                <ModalsProvider>
                  <App />
                </ModalsProvider>
              </ErrorBoundary>
            </Router>
          </PartnerRoleProvider>
        </AuthProvider>
      </ThemeProvider>
    </LocalizationProvider>
    <ReactQueryDevtools initialIsOpen={false} />
  </QueryClientProvider>,
  document.getElementById('root'),
);

window.hash = process.env.GIT_COMMIT_HASH;
